import { useState, useEffect, useContext } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import { ModalDialog, FlowForm } from 't-lib-ui/structure';
import { Input } from 't-lib-ui/widgets';
import { getAdminMagicSecret, setAdminMagicSecret, clearAdminMagicSecret } from '../../connector/auth';
import { adminNotificationEngine } from '../../connector/admin-notification-engine';

interface AdminSecretModalProps {
  onSecretVerified: (secret: string) => void;
  /** Set to a new value (e.g. increment) when an admin API call fails due to a bad secret. Clears the stored secret and re-shows the dialog. */
  authFailed?: number;
}

/**
 * Modal dialog that prompts for admin secret if not already stored in localStorage.
 * Redirects to /a if user cancels.
 */
const AdminSecretModal: React.FC<AdminSecretModalProps> = ({ onSecretVerified, authFailed }) => {
  const routerCtx = useContext(RouterContext);
  const [showDialog, setShowDialog] = useState(false);
  const [secretInput, setSecretInput] = useState('');

  useEffect(() => {
    const storedSecret = getAdminMagicSecret();
    if (storedSecret) {
      onSecretVerified(storedSecret);
    } else {
      setShowDialog(true);
    }
  }, [onSecretVerified]);

  useEffect(() => {
    if (authFailed) {
      clearAdminMagicSecret();
      setSecretInput('');
      setShowDialog(true);
    }
  }, [authFailed]);

  const handleSubmit = () => {
    if (secretInput.trim()) {
      setAdminMagicSecret(secretInput.trim());
      adminNotificationEngine.recheckNow(); // secret now available -> poll + clear the sidebar warning
      onSecretVerified(secretInput.trim());
      setShowDialog(false);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    goTo(routerCtx, '/a');
  };

  return (
    <ModalDialog
      show={showDialog}
      title="Admin Secret Required"
      onClose={handleCancel}
      onConfirm={handleSubmit}
      closeButton="close"
      minWidth="400px"
    >
      <div style={{ padding: '0 20px 20px 20px' }}>
        <FlowForm>
          <div style={{ marginBottom: '16px' }}>
            <p>Enter the admin secret to access this page:</p>
          </div>
          <Input
            type="text"
            value={secretInput}
            placeholder="Admin secret"
            onInput={setSecretInput}
            onEnter={handleSubmit}
            layout="singleCol"
            size="medium"
          />
        </FlowForm>
      </div>
    </ModalDialog>
  );
};

export default AdminSecretModal;
